import ApiService from '@/services/apiService';
import debounce from 'lodash/debounce';
import intersection from 'lodash/intersection';
import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import get from 'lodash/get';

export const itemTypeLoadMore = 'LoadMore.';
export const itemTypeDefault = 'Default';
export const paginationLimit = 100;

export const dtwinTreeCacheKey = 'DtwinTreeCache';

export const TreeMixin = {
  props: {
    userId: {
      type: String,
      required: false,
    },
    tenantId: {
      type: Number,
      required: false,
    },
    favourites: {
      type: Boolean,
      required: false,
      default: false,
    },
    parentId: {
      required: false,
    },
    fetchLimit: {
      type: Number,
      required: false,
      default: () => paginationLimit,
    },
    activeDtwinId: {
      type: Number,
      required: false,
      default: null,
    },
    openTreeNodes: {
      type: Array,
      required: false,
      default: () => [],
    },
    searchTerm: {
      type: String,
      required: false,
      default: '',
    },
    // a filter that defines which nodes can be marked as "active"
    // the twin must have one of the defined items as tag
    selectionTagFilter: {
      type: Array,
      required: false,
      default: () => [],
    },
    // custom filter function to determine if a node can be marked as "active"
    selectionObjectFilter: {
      type: Function,
      required: false,
      default: null,
    },
    canSelectDisabledTwins: {
      type: Boolean,
      required: false,
      default: false,
    },

    // for the tree in the /digitaltwin-mgmt overview, the cache can be enabled
    // to persist the last open state rather than re-loading
    cached: {
      type: Boolean,
      required: false,
      default: false,
    },
    // if the user has `DigitalTwin.read` permission, then with this flag the whole dtwin-object is loaded
    // otherwise just the bare data like title, parent, scopeId (i.e. for display in lists/trees)
    loadFullDtwinData: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: function () {
    return {
      // should the children-fetching also try to fill the "fetchLimit" with as many descendents as possible?
      // for the states-tree, only load 1 level to make the processing not such a headache
      loadDescendents: true,

      open: [],
      active: null,

      fetchError: null,

      twinTreeItems: [],
      twinItemMapping: {},

      isLoading: false,
      search: null,
      rules: [(v) => !!v || this.$i18n_t('IS.FM.AccessMgmt.accessObjectReq')],
      noRules: [],
    };
  },
  computed: {
    selected() {
      if (!this.active) {
        return undefined;
      }
      const id = this.active;
      return this.twinItemMapping[id];
    },
    searchIsActive() {
      return this.isSearchIsActive(this.searchTerm);
    },
  },
  watch: {
    open() {
      this.persistToCache();
    },
    favourites() {
      this.initTree();
    },
    openTreeNodes() {
      this.open = this.openTreeNodes;
    },
    activeDtwinId() {
      //this.active = this.activeDtwinId;
      if (this.active) {
        this.refreshTree();
      } else {
        this.initTree();
      }

      // Disabled by https://digimondo.atlassian.net/browse/RD-5913
      // this.$router.replace({ path: '/digitaltwin-mgmt', query: { active: this.active } });
    },
    searchTerm: debounce(
      function () {
        if (this.isLoading) {
          return;
        }
        this.active = null;
        this.open = [];
        const searchItem = this.searchTerm ? this.searchTerm.trim() : null;
        if (this.searchIsActive) {
          this.fetchPartialTree(undefined, searchItem);
        } else if (!searchItem || searchItem === '') {
          this.initTree();
        }
      },
      500,
      { leading: true }
    ),
    parentId() {
      this.initTree();
    },
    userId() {
      this.initTree();
    },
  },
  methods: {
    // ===================
    // TO BE IMPLEMENTED BY THE COMPONENT:
    // -- initTree
    // -- itemType
    // -- processTreeItems
    // -- fetchChildren
    // -- getTwinTreeIcon

    // --------
    // UI
    // --------

    isSearchIsActive(term) {
      const searchItem = term ? term.trim() : null;
      return searchItem && searchItem.length >= 3;
    },

    twinItemLabelClass(item) {
      let activeColor = this.color;
      let baseClass = 'selectable-label ';
      if (item.properties && item.properties.disabled) {
        activeColor = 'disabled-twin primary';
        baseClass += 'disabled-twin ';
      }
      return baseClass + (this.active === item.id ? `${activeColor}--text text-bold` : '');
    },
    twinItemIconClass(item) {
      if (item.properties && item.properties.disabled) {
        return 'primary--text disabled-twin';
      }
      return `${this.color}--text`;
    },

    getTwinTreeIconDefault(dtwin) {
      const faIcon = `fa-${get(dtwin, 'data.faIcon') || 'dice-d8'}`;
      return !dtwin.parentId
        ? `fas ${faIcon}`
        : !dtwin.children || dtwin.children.length === 0
        ? `fal ${faIcon}`
        : `fal ${faIcon}`;
    },

    canSelect(item, customData) {
      if (!this.canSelectDisabledTwins && get(item, 'properties.disabled')) {
        return false;
      }

      if (this.selectionTagFilter && this.selectionTagFilter.length) {
        if (intersection(get(item, 'data.tags') || [], this.selectionTagFilter).length === 0) {
          return false;
        }
      }
      if (this.selectionObjectFilter != null) {
        if (!this.selectionObjectFilter(item, this.findParents(item.parentId, []), customData)) {
          return false;
        }
      }
      return true;
    },
    findParents(dtwinId, list) {
      if (!dtwinId) {
        return list;
      }
      const findTwin = (dtwinId, items) => {
        let dtwin = items.find((t) => t.id === dtwinId);
        if (dtwin) {
          return dtwin;
        }
        for (let i = 0; i < items.length; i++) {
          if (items[i].children) {
            dtwin = findTwin(dtwinId, items[i].children);
            if (dtwin) {
              return dtwin;
            }
          }
        }
        return null;
      };

      const dtwin = findTwin(dtwinId, this.twinTreeItems);
      if (dtwin) {
        return this.findParents(dtwin.parentId, [dtwin].concat(list));
      }
      return list;
    },

    async didToggleSelection(item) {
      const itemId = (item || {}).id;
      if (this.active && this.active === itemId) {
        this.active = null;
      } else {
        this.active = itemId;
      }
      if (!itemId) {
        return;
      }
      let twinObj = await ApiService.digitalTwinGet(this, itemId);
      this.$emit('onSelectedChange', twinObj);
    },

    // --------
    // GENERIC TREE HANDLING
    // --------

    persistToCache() {
      if (!!this.cached && this.cached === true) {
        this.$store.state.app.dtwins[dtwinTreeCacheKey] = {
          twinTreeItems: this.twinTreeItems,
          openTreeNodes: this.open,
        };
      }
    },

    refreshTree(calledFromFetchPartialTree = false) {
      if (this.active) {
        // case 1: find the active node in the twin-tree and open its hierarchy
        if (this.selected) {
          // - check if a root-item is selected
          // - the active-item(s parent) shall not already be in the open-node list
          // - then find all parents up to the root-element and open all
          if (!this.twinTreeItems.find((i) => i.id === this.active)) {
            if (!this.open.find((o) => o.id === this.selected.parentId)) {
              const recursiveFindParents = (parentId, list) => {
                if (this.twinItemMapping[parentId]) {
                  return recursiveFindParents(this.twinItemMapping[parentId].parentId, [parentId].concat(list));
                }
                return list;
              };
              if (this.open.length === 0) {
                this.open = recursiveFindParents(this.selected.parentId, []);
              }
            }
          }
        } else {
          // case 2: the active item is not part of our data-set, so fetch its tree
          if (!calledFromFetchPartialTree) {
            // to avoid endless loops, check whether the refresh-tree method was called from the fetch-method already
            this.fetchPartialTree(this.active);
          }
        }
      }
      this.twinTreeItems = clone(this.twinTreeItems);
      this.persistToCache();
    },

    fetchPartialTree(dtwinId, searchItem) {
      let openTreeNodes = this.open.length === 0 ? this.openTreeNodes : [];

      let options = [];
      if (!this.loadDescendents) {
        options.push('no-children');
      }
      if (!this.loadFullDtwinData) {
        options.push('bare');
      }
      if (this.favourites) {
        options.push('favourites');
      }

      let fetchPromise = ApiService.digitalTwinFetchPartialTree(
        this,
        this.isSearchIsActive(searchItem) ? null : dtwinId,
        this.isSearchIsActive(searchItem) ? searchItem.trim() : null,
        this.parentId,
        options,
        this.tenantId
      );

      // if a search is going on, do not bother with the cache
      if (!this.isSearchIsActive(searchItem) && !this.favourites && this.cached === true) {
        const cachedDtwins = this.$store.state.app.dtwins[dtwinTreeCacheKey];
        if (cachedDtwins) {
          const findRequestedDtwin = (id, dtwins) => {
            if (!dtwins) {
              return false;
            }
            return dtwins.find((d) => {
              return d.id === id || findRequestedDtwin(id, d.children);
            });
          };
          // only use the cached twins, if the requested twin is found in the cached tree
          if (findRequestedDtwin(dtwinId, cachedDtwins.twinTreeItems)) {
            fetchPromise = Promise.resolve({ digitaltwins: cachedDtwins.twinTreeItems, cachedResponse: true });
            // do not load cached tree-state if there already is a state set
            if (this.open.length === 0) {
              openTreeNodes = cachedDtwins.openTreeNodes;
            }
          }
        }
      }

      this.isLoading = true;
      return fetchPromise
        .then((data) => {
          const digitaltwins = this.cloneAndFilterDTwins(data.digitaltwins);
          this.twinTreeItems = digitaltwins;
          this.twinItemMapping = {};
          if (data.error && data.digitaltwins.length === 0) {
            this.fetchError = data.error;
          } else {
            if (searchItem) {
              openTreeNodes = [];
            }
            const recursiveProcessing = async (dtwins) => {
              const promise = this.processTreeItems(dtwins);
              if (promise) {
                await promise;
              }
              await dtwins.forEach(async (i) => {
                this.twinItemMapping[i.id] = i;

                if (searchItem) {
                  // only open the tree, if the found item is not at root-level
                  // otherwise loading children will not work, because the item is already "open"
                  if (this.twinTreeItems.findIndex((rootItem) => rootItem.id === i.id) >= 0) {
                    // unless this is a root-item, but the actual found item is a child
                    if (i.children && i.children.length > 0) {
                      openTreeNodes.push(i.id);
                    }
                  } else {
                    openTreeNodes.push(i.id);
                  }
                }

                // if there ARE children, the ".children"-array exists, but will be empty
                // in that case there is no need to add a "Lore More" button, the native tree-functionality
                // will take care of the async loading (:load-children="fetchChildren") for empty children-arrays
                // if it is there and filled, then process the children here
                if (i.children && i.children.length) {
                  await recursiveProcessing(i.children);

                  // if the world would be a nice place, we could utlise the nice offset-feature
                  // and just take i.children.length as offset to load all the other siblings to the existing ones
                  // however, as this is a partial tree and the original child-list is sorted alphabetically, it might
                  // be the case that this particular sublist of children is from the middle of the list
                  // thus, onless some complicated filtering-parameters are handed over to build the rest of the list correctly,
                  // we must start over and load all children from scratch, so use an offset of 0
                  const fetchedOffset = 0;
                  if (!this.searchIsActive && i.children.length < i.childCount && !data.cachedResponse) {
                    const upperLimit = Math.min(fetchedOffset + this.fetchLimit, i.childCount);
                    this.addPaginationChildItem(i, i.childCount, fetchedOffset, upperLimit);
                  }
                }
                if (!this.searchIsActive && i.siblingCount && !data.cachedResponse) {
                  const fetchedOffset = 0; // see lengthy comment above
                  const upperLimit = Math.min(fetchedOffset + this.fetchLimit, i.siblingCount);
                  this.addPaginationChildItem(null, i.siblingCount, fetchedOffset, upperLimit);
                }
              });
            };
            recursiveProcessing(digitaltwins);

            if (this.open.length === 0 || openTreeNodes.length > 0) {
              this.open = openTreeNodes;
            }
            if (!this.active && this.activeDtwinId) {
              this.didToggleSelection(this.twinItemMapping[this.activeDtwinId]);
            }
            this.fetchError = null;

            this.refreshTree(true);
          }

          return this.twinTreeItems;
        })
        .catch((error) => {
          console.error('Error finding DTwins', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    triggerLoadMore(activeItem) {
      if (activeItem && activeItem.type && activeItem.type === itemTypeLoadMore) {
        // an offset of 0 indicates that the whole list should be loaded again, so clear all current items
        if (activeItem.offset === 0) {
          if (activeItem.parentId) {
            this.twinItemMapping[activeItem.parentId].children = [];
          } else {
            // reload the root-items
            this.twinTreeItems = [];
          }
        }

        if (activeItem.parentId) {
          this.twinItemMapping[activeItem.parentId].children = this.twinItemMapping[
            activeItem.parentId
          ].children.filter((c) => {
            return this.itemType(c) !== itemTypeLoadMore;
          });
          this.twinTreeItems = clone(this.twinTreeItems);
          this.fetchChildrenDefault(activeItem.parentId, activeItem.offset);
        } else {
          // pagination on the root-level of the tree
          this.twinTreeItems = this.twinTreeItems.filter((c) => {
            return this.itemType(c) !== itemTypeLoadMore;
          });
          this.fetchChildrenDefault(this.parentId, activeItem.offset);
        }

        return true;
      }
      return false;
    },

    // --------
    // DEFAULT
    // --------

    fetchChildrenDefault(itemId, offset = 0, initially = false) {
      this.isLoading = true;

      let options = [];
      if (!this.loadDescendents) {
        options.push('no-descendents');
      }
      if (!this.loadFullDtwinData) {
        options.push('bare');
      }
      if (this.favourites && initially) {
        options.push('favourites');
      }
      return ApiService.digitalTwinFetchChildren(
        this,
        itemId,
        offset,
        this.fetchLimit,
        this.userId,
        options,
        false,
        this.tenantId
      )
        .then(async (data) => {
          const digitaltwins = this.cloneAndFilterDTwins(data.digitaltwins);
          this.fetchError = null;
          const promise = this.processTreeItems(digitaltwins);
          if (promise) {
            await promise;
          }
          if (!itemId || parseInt(itemId) === parseInt(this.parentId)) {
            // these are the root-twins of the tree (at least the first page of it)
            if (offset === 0 && (!this.parentId || !this.twinItemMapping[this.parentId].children)) {
              this.twinTreeItems = digitaltwins;
            } else if (!itemId && offset > 0) {
              // this is a request on the root-level, but with an offset
              // (i.e. more than 100 twins at root-level)
              this.twinTreeItems = (this.twinTreeItems || []).concat(digitaltwins);
            }

            if (!this.active && this.activeDtwinId) {
              this.didToggleSelection(this.twinItemMapping[this.activeDtwinId]);
            }
            if (this.open.length === 0) {
              this.open = this.openTreeNodes;
            }
          }
          const orgItem = this.twinItemMapping[itemId];
          if (orgItem) {
            if (orgItem.children) {
              orgItem.children.push(...digitaltwins);
            } else {
              orgItem.children = digitaltwins;
            }
            this.processTreeItems([orgItem]);
          }
          this.refreshTree();

          const fetchedOffset = offset !== undefined ? offset : 0;
          this.checkIfPaginationItemNeedsToBeAdded(fetchedOffset, this.itemOrParent(orgItem), data.totalCount);
        })
        .catch((error) => {
          console.error('Error finding DTwins', error);
        })
        .finally(() => (this.isLoading = false));
    },

    itemOrParent(item) {
      if (item && item.id) {
        return item;
      }
      if (this.parentId) {
        return { id: this.parentId };
      }
      return null;
    },

    addPaginationChildItem(item, totalCount, nextLower, nextUpper) {
      // check if a pagination-button-item is already there
      if (item) {
        if (this.twinItemMapping[item.id].children.find((c) => c.type === itemTypeLoadMore)) {
          return;
        }
      } else {
        if (this.twinTreeItems.find((i) => i.type === itemTypeLoadMore)) {
          return;
        }
      }

      const loadMoreItem = {
        title: this.$i18n_t('DigitalTwin.DigitalTwin.loadMoreItems', {
          nextLower: nextLower + 1,
          nextUpper,
          total: totalCount,
        }),
        data: { faIcon: 'sync' },
        parentId: item ? item.id : this.parentId,
        id: itemTypeLoadMore + (item ? item.id : this.parentId),
        type: itemTypeLoadMore,
        offset: nextLower,
        // prevent some post-processing to add an empty children array to show a vuetify tree expansion-arrow
        childCount: 0,
      };
      this.twinItemMapping[loadMoreItem.id] = loadMoreItem;
      if (item) {
        this.twinItemMapping[item.id].children.push(loadMoreItem);
      } else {
        // add pagination on the root-level of the tree
        this.twinTreeItems.push(loadMoreItem);
      }
    },

    checkIfPaginationItemNeedsToBeAdded(fetchedOffset, item, totalCount) {
      const fetchedUpTo = fetchedOffset + this.fetchLimit;
      if (!this.searchIsActive && fetchedUpTo < totalCount) {
        const upperLimit = Math.min(fetchedUpTo + this.fetchLimit, totalCount);
        this.addPaginationChildItem(item, totalCount, fetchedUpTo, upperLimit);
      }
    },

    processTreeItemsDefault(items) {
      return Promise.all(
        items.map(async (item) => {
          this.twinItemMapping[item.id] = item;
          const fetchOffset = (item.children || []).length;

          if (item.childCount === 0) {
            delete item.children;
          } else if (!item.children) {
            // there are children, but not loaded yet
            // to trigger the v-treeview async loading via :fetchChildren, there must be an empty array
            item.children = [];
          } else if (item.children && fetchOffset > 0) {
            const promise = this.processTreeItems(item.children);
            if (promise) {
              await promise;
            }
          }
          if (!this.twinItemMapping[item.parentId]) {
            delete item.parentId;
          }
        })
      );
    },

    cloneAndFilterDTwins(digitaltwins) {
      const filterDisabledDTwins = function (dtwins) {
        if (!dtwins) {
          return undefined;
        }
        return dtwins.filter((dtwin) => {
          if (get(dtwin, 'properties.disabled')) {
            return false;
          }
          dtwin.children = filterDisabledDTwins(dtwin.children);
          return true;
        });
      };
      if (this.permissions['all.manage']) {
        // an admin can see all digital twins
        return cloneDeep(digitaltwins);
      }
      // otherwise filter out the disabled ones
      return cloneDeep(filterDisabledDTwins(digitaltwins));
    },
  },
};
